import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { FC, useEffect, useState } from 'react'
import Modal from '../common/Modal'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../states/containers/store'
import { TEMPLATE_LIST_1, TEMPLATE_LIST_2 } from '../../constants/taskTemplate'
import { useHistory, useLocation } from 'react-router-dom'
import { setPath, setUrlId } from '../../../utils/url'
import { URL } from '../../constants/url'
import LoadingSquare from '../common/LoadingSquare'
import { RequestGetTaskProjectTemplateSetting } from '../../../states/types/task'
import { fetchTaskProjectTemplateSetting } from '../../../states/containers/task/taskSlice'
import { RoleMember } from '../../../states/types/setting'
import ApiRequestModal from '../common/ApiRequestModal'
import Tips from '../common/Tips'

interface Team {
  id: number
  scope: string
  name: string
  include_nested_teams: boolean | null
}

interface TemplateDetail {
  id?: number
  name: string
  color?: string
  code?: string
  description?: string
  label_group_category_ids?: number[]
  permission: {
    viewable: {
      teams: Team[]
      members: {
        id: number
        name: string
      }[]
      category_managers?: {
        id: number
        name: string
      }[]
    }
    usable: {
      teams: Team[]
      members: {
        id: number
        name: string
      }[]
    }
    enable_settable_permission: boolean
    settable: {
      teams: Team[]
      members: {
        id: number
        name: string
      }[]
    }
  }
  labels: {
    name: string
  }[]
}

interface Props {
  closeCallback: VoidFunction
  addCallback: (state: { detail?: TemplateDetail | null, labelGroupId?: number, templateId?: number }) => void
  deleteCallback?: (id: number, name: string) => void
  importCallback: () => void
  isDisplay: boolean
  startCustom?: boolean
}

const SCOPE_LABEL: Record<string, string> = {
  both: "マネージャー・メンバー",
  manager: "マネージャー",
  member: "メンバー",
}

const CreateProjectModal: FC<Props> = (props) => {
  const {
    closeCallback,
    addCallback,
    deleteCallback,
    importCallback,
    isDisplay,
    startCustom,
  } = props

  const {
    task,
    login,
  } = useSelector((state: RootState) => state)

  const history = useHistory()
  const dispatch = useDispatch()
  const location = useLocation()

  const [step, setStep] = useState<string>("select")
  const [selectList, setSelectList] = useState<TemplateDetail[]>([])

  const [isOpenApiRequestModal, setIsOpenApiRequestModal] = useState<boolean>(false)

  const [isLoading, setIsLoading] = useState<boolean>(true)

  const getAllMemberList = (teamId: number | null): RoleMember[] => {
    if (!task.taskLabelSettingNew?.teams) return []
    const tmpTeams = task.taskLabelSettingNew.teams.filter((v) => v.parent_team_id === teamId)
    if (!tmpTeams.length) return []
    const tmpMembers = [...tmpTeams.map((v) => [...v.managers, ...v.members]).flat(), ...tmpTeams.map((v) => getAllMemberList(v.id).flat()).flat()]
    return Array.from(new Map(tmpMembers.map((v) => [v.id, v])).values())
  }

  const close = () => {
    setStep("select")
    closeCallback()
  }

  const clickSelect = (detail: TemplateDetail) => {
    if (step === "category_1" || step === "category_2" || step === "custom") {
      addCallback({ detail: detail })
      return
    }
    if (step === "copy") {
      addCallback({ labelGroupId: detail.id })
    }
  }

  const clickPrev = () => {
    if (step === "category_1" || step === "category_2") {
      setStep("generic")
      return
    }
    setStep("select")
  }

  const createTemplate = () => {
    history.push({
      pathname: `${setPath(URL.TASK_PROJECT_TEMPLATE_SETTING_CREATE.path, "ja")}`,
      state: { from: location.pathname, keep: true },
    })
  }

  const clickEdit = (id: number) => {
    if (!id) {
      createTemplate()
      return
    }
    history.push({
      pathname: `${setPath(setUrlId(URL.TASK_PROJECT_TEMPLATE_SETTING_DETAIL.path, 'id', id), "ja")}`,
      state: { from: location.pathname, keep: true },
    })
  }

  const deleteTemplate = (id: number, name: string) => {
    if (!deleteCallback) return
    deleteCallback(id, name)
  }

  const buildTargetMember = (team: Team, scope: string) => {
    const targetTeam = task.taskLabelSettingNew?.teams.find((v) => v.id === team.id)
    if (!targetTeam) return []
    if (scope === "manager") {
      return targetTeam.managers
    }
    if (scope === "member") {
      return targetTeam.members
    }
    return Array.from(new Set([...targetTeam.managers, ...targetTeam.members]))
  }

  useEffect(() => {
    if (step === "select") {
      setSelectList([])
      return
    }
    if (step === "category_1") {
      setSelectList(TEMPLATE_LIST_1)
      setIsLoading(false)
      return
    }
    if (step === "category_2") {
      setSelectList(TEMPLATE_LIST_2)
      setIsLoading(false)
      return
    }
    if (step === "custom") {
      setIsLoading(true)
      const params: RequestGetTaskProjectTemplateSetting = {
        limit: 1000,
      }
      dispatch(
        fetchTaskProjectTemplateSetting(
          params,
        ),
      )
      return
    }
    if (step === "copy") {
      setSelectList(
        task.taskLabelSetting?.label_groups.map((v) => (
          {
            id: v.id,
            name: v.name,
            permission: v.permission,
            label_group_category_ids: v.label_group_category_ids,
            labels: v.labels,
          }
        )) || [],
      )
      setIsLoading(false)
    }
  }, [step])

  useEffect(() => {
    if (step !== "custom") return
    setSelectList(
      task.taskProjectTemplateSetting?.label_group_templates.map((v) => (
        {
          id: v.id,
          name: v.name,
          color: v.color,
          code: v.code || "",
          description: v.description || "",
          label_group_category_ids: v.label_group_category_ids,
          permission: v.permission,
          labels: v.labels,
        }
      )) || [],
    )
    setIsLoading(false)
  }, [task.taskProjectTemplateSetting?.label_group_templates])

  useEffect(() => {
    if (!startCustom) return
    setStep("custom")
  }, [startCustom])

  return (
    <>
      <Modal
        show={isDisplay}
        wrapperClass="create_project_modal"
        closeCallback={close}
        closeable
      >
        <>
          {
            step === "select" && (
              <>
                <div className="select_type_wrapper">
                  <button
                    type="button"
                    onClick={() => addCallback({ detail: null })}
                    className="new"
                  >
                    <div className="type_icon_area">
                      <FontAwesomeIcon icon={['fas', 'edit']} />
                    </div>
                    <div className="type_text_area">
                      すべて入力して作成する
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setStep("generic")}
                    className="generic"
                  >
                    <div className="type_icon_area">
                      <FontAwesomeIcon icon={['fas', 'object-group']} />
                    </div>
                    <div className="type_text_area">
                      <div>
                        汎用テンプレートから
                      </div>
                      <div>
                        選択して作成する
                      </div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setStep("custom")}
                    className="custom"
                  >
                    <div className="type_icon_area">
                      <FontAwesomeIcon icon={['fas', 'chalkboard-teacher']} />
                    </div>
                    <div className="type_text_area">
                      <div>
                        ユーザーテンプレートから
                      </div>
                      <div>
                        選択して作成する
                      </div>
                    </div>
                  </button>
                  {
                    login.role_permission?.task_project_import && (
                      <button
                        type="button"
                        onClick={importCallback}
                        className="csv"
                      >
                        <div className="type_icon_area">
                          <FontAwesomeIcon icon={['fas', 'file-csv']} />
                        </div>
                        <div className="type_text_area">
                          <div>
                            CSVファイルで
                          </div>
                          <div>
                            まとめて作成する
                          </div>
                        </div>
                      </button>
                    )
                  }
                  <button
                    type="button"
                    onClick={() => setIsOpenApiRequestModal(true)}
                    className="api"
                  >
                    <div className="type_icon_area">
                      <FontAwesomeIcon icon={['fas', 'object-group']} />
                    </div>
                    <div className="type_text_area">
                      <div>
                        API連携機能で
                      </div>
                      <div>
                        作成したい
                      </div>
                    </div>
                  </button>
                </div>
              </>
            )
          }
          {
            step === "generic" && (
              <>
                <button
                  type="button"
                  onClick={clickPrev}
                  style={{
                    fontSize: "11px",
                  }}
                >
                  &lt; ひとつ前へ戻る
                </button>
                <div className="select_type_wrapper">
                  <button
                    type="button"
                    onClick={() => setStep("category_2")}
                    className="occupation"
                  >
                    <div className="type_icon_area">
                      <FontAwesomeIcon icon={['fas', 'walking']} />
                    </div>
                    <div className="type_text_area">
                      <div>
                        職種ベースで
                      </div>
                      <div>
                        プロジェクトを作成する
                      </div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setStep("category_1")}
                    className="case"
                  >
                    <div className="type_icon_area">
                      <FontAwesomeIcon icon={['fas', 'scroll']} />
                    </div>
                    <div className="type_text_area">
                      <div>
                        案件ベースで
                      </div>
                      <div>
                        プロジェクトを作成する
                      </div>
                    </div>
                  </button>
                </div>
              </>
            )
          }
          {
            (step === "category_1" || step === "category_2" || step === "custom" || step === "copy") && (
              <>
                <button
                  type="button"
                  onClick={clickPrev}
                  style={{
                    fontSize: "11px",
                  }}
                >
                  &lt; ひとつ前へ戻る
                </button>
                {
                  isLoading ? (
                    <>
                      <LoadingSquare>
                        <>
                          テンプレートリストを読み込んでいます…
                        </>
                      </LoadingSquare>
                    </>
                  ) : (
                    <>
                      <div className="template_list">
                        <>
                          {
                            selectList.map((detail) => (
                              <div
                                key={detail.name}
                                className="template_block"
                              >
                                <div className="template_title_area">
                                  {
                                    detail.color && (
                                      <div
                                        className="template_title_color"
                                        style={{
                                          backgroundColor: detail.color,
                                        }}
                                      />
                                    )
                                  }
                                  <div className="template_title">
                                    {detail.name}
                                  </div>
                                </div>
                                {
                                  detail.description ? (
                                    <div className="template_description">
                                      {
                                        detail.description.split("\n").map((v) => (
                                          <div>
                                            {v}
                                          </div>
                                        ))
                                      }
                                    </div>
                                  ) : (
                                    <></>
                                  )
                                }
                                {
                                  // eslint-disable-next-line max-len
                                  (detail.permission.viewable.teams.length || detail.permission.viewable.members.length || detail.permission.viewable.category_managers?.length) ? (
                                    <>
                                      <div className="template_detail">
                                        <div className="template_detail_title">
                                          {`プロジェクトレポート表示${detail.permission.enable_settable_permission ? "" : "・編集権限"}対象`}
                                        </div>
                                        {
                                          !!detail.label_group_category_ids?.length && (
                                            <div
                                              style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "4px",
                                                flexWrap: "wrap",
                                              }}
                                            >
                                              {
                                                task.taskLabelSettingNew?.label_group_categories
                                                .filter((v) => detail.label_group_category_ids?.includes(v.id)).map((v) => (
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      gap: "4px",
                                                      border: "solid 1px #999999",
                                                      borderRadius: "20px",
                                                      padding: "2px 8px",
                                                    }}
                                                  >
                                                    <FontAwesomeIcon icon={['fas', 'folder']} style={{ color: v.color }} />
                                                    {v.category_name}
                                                  </div>
                                                ))
                                              }
                                            </div>
                                          )
                                        }
                                        {
                                          !!detail.permission.viewable.category_managers?.length && (
                                            <div
                                              style={{
                                                border: "solid 1px #CCCCCC",
                                              }}
                                            >
                                              <div
                                                style={{
                                                  display: "flex",
                                                  justifyContent: "space-between",
                                                  alignItems: "center",
                                                  padding: "8px",
                                                  borderBottom: "solid 1px #CCCCCC",
                                                  backgroundColor: "#EEEEEE",
                                                }}
                                              >
                                                <div>
                                                  <Tips
                                                    description="プロジェクトが所属しているカテゴリーの表示ユーザーです。"
                                                    className=""
                                                  >
                                                    <>
                                                      カテゴリー表示ユーザー
                                                    </>
                                                  </Tips>
                                                </div>
                                              </div>
                                              <div
                                                className="template_tips"
                                                style={{
                                                  padding: "8px",
                                                }}
                                              >
                                                {
                                                  detail.permission.viewable.category_managers.map((member) => (
                                                    <div
                                                      className="template_tip"
                                                      key={`category_manager_${member.id}`}
                                                    >
                                                      {member.name}
                                                    </div>
                                                  ))
                                                }
                                              </div>
                                            </div>
                                          )
                                        }
                                        {
                                          !!detail.permission.viewable.teams?.length && (
                                            <div
                                              style={{
                                                border: "solid 1px #CCCCCC",
                                                borderRadius: "4px",
                                                padding: "16px",
                                                position: "relative",
                                                backgroundColor: "#FFFFFF",
                                                display: "flex",
                                                flexFlow: "column",
                                                gap: "8px",
                                              }}
                                            >
                                              <div
                                                style={{
                                                  backgroundColor: "#FFFFFF",
                                                  padding: "0 4px",
                                                  position: "absolute",
                                                  top: 0,
                                                  left: "8px",
                                                  transform: "translateY(-50%)",
                                                  fontSize: "10px",
                                                  color: "#666666",
                                                }}
                                              >
                                                チーム
                                              </div>
                                              {
                                                detail.permission.viewable.teams.map((team) => (
                                                  <div
                                                    style={{
                                                      border: "solid 1px #CCCCCC",
                                                    }}
                                                  >
                                                    <div
                                                      style={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        alignItems: "center",
                                                        padding: "8px",
                                                        borderBottom: "solid 1px #CCCCCC",
                                                        backgroundColor: "#EEEEEE",
                                                      }}
                                                    >
                                                      <div>
                                                        {`チーム「${team.name}」の${SCOPE_LABEL[team.scope]}${team.include_nested_teams ? " + 配下チーム" : ""}`}
                                                      </div>
                                                    </div>
                                                    <div
                                                      style={{
                                                        display: "flex",
                                                        flexFlow: "column",
                                                        gap: "12px",
                                                        padding: "12px 8px 8px",
                                                      }}
                                                    >
                                                      {
                                                        ["both", "manager"].includes(team.scope) && (
                                                          <div
                                                            style={{
                                                              border: "solid 1px #CCCCCC",
                                                              borderRadius: "4px",
                                                              padding: "8px",
                                                              position: "relative",
                                                              backgroundColor: "#FFFFFF",
                                                            }}
                                                          >
                                                            <div
                                                              style={{
                                                                backgroundColor: "#FFFFFF",
                                                                padding: "0 4px",
                                                                position: "absolute",
                                                                top: 0,
                                                                left: "8px",
                                                                transform: "translateY(-50%)",
                                                                fontSize: "10px",
                                                                color: "#666666",
                                                              }}
                                                            >
                                                              マネージャー
                                                            </div>
                                                            <div
                                                              className="template_tips"
                                                              style={{
                                                                padding: "8px",
                                                              }}
                                                            >
                                                              {
                                                                buildTargetMember(team, "manager").map((member) => (
                                                                  <div
                                                                    className="template_tip"
                                                                    key={`member_${member.id}`}
                                                                  >
                                                                    {member.name}
                                                                  </div>
                                                                ))
                                                              }
                                                            </div>
                                                          </div>
                                                        )
                                                      }
                                                      {
                                                        ["both", "member"].includes(team.scope) && !team.include_nested_teams && (
                                                          <div
                                                            style={{
                                                              border: "solid 1px #CCCCCC",
                                                              borderRadius: "4px",
                                                              padding: "8px",
                                                              position: "relative",
                                                              backgroundColor: "#FFFFFF",
                                                            }}
                                                          >
                                                            <div
                                                              style={{
                                                                backgroundColor: "#FFFFFF",
                                                                padding: "0 4px",
                                                                position: "absolute",
                                                                top: 0,
                                                                left: "8px",
                                                                transform: "translateY(-50%)",
                                                                fontSize: "10px",
                                                                color: "#666666",
                                                              }}
                                                            >
                                                              このチームのメンバー
                                                            </div>
                                                            <div
                                                              className="template_tips"
                                                              style={{
                                                                padding: "8px",
                                                              }}
                                                            >
                                                              {
                                                                buildTargetMember(team, "member").map((member) => (
                                                                  <div
                                                                    className="template_tip"
                                                                    key={`member_${member.id}`}
                                                                  >
                                                                    {member.name}
                                                                  </div>
                                                                ))
                                                              }
                                                            </div>
                                                          </div>
                                                        )
                                                      }
                                                      {
                                                        ["both", "member"].includes(team.scope) && team.include_nested_teams && (
                                                          <div
                                                            style={{
                                                              border: "solid 1px #CCCCCC",
                                                              borderRadius: "4px",
                                                              padding: "8px",
                                                              position: "relative",
                                                              backgroundColor: "#FFFFFF",
                                                            }}
                                                          >
                                                            <div
                                                              style={{
                                                                backgroundColor: "#FFFFFF",
                                                                padding: "0 4px",
                                                                position: "absolute",
                                                                top: 0,
                                                                left: "8px",
                                                                transform: "translateY(-50%)",
                                                                fontSize: "10px",
                                                                color: "#666666",
                                                              }}
                                                            >
                                                              このチームのメンバー + 配下チームに所属している全てのマネージャー・メンバー
                                                            </div>
                                                            <div
                                                              className="template_tips"
                                                              style={{
                                                                padding: "8px",
                                                              }}
                                                            >
                                                              {
                                                                Array.from(new Map([
                                                                  ...getAllMemberList(team.id || null),
                                                                  ...buildTargetMember(team, "member"),
                                                                ].map((w) => [w.id, w])).values()).sort((a, b) => a.id - b.id).map((member) => (
                                                                  <div
                                                                    className="template_tip"
                                                                    key={`member_${member.id}`}
                                                                  >
                                                                    {member.name}
                                                                  </div>
                                                                ))
                                                              }
                                                            </div>
                                                          </div>
                                                        )
                                                      }
                                                    </div>
                                                  </div>
                                                ))
                                              }
                                            </div>
                                          )
                                        }
                                        {
                                          !!detail.permission.viewable.members?.length && (
                                            <>
                                              <div
                                                style={{
                                                  border: "solid 1px #CCCCCC",
                                                  borderRadius: "4px",
                                                  padding: "16px",
                                                  position: "relative",
                                                  backgroundColor: "#FFFFFF",
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    backgroundColor: "#FFFFFF",
                                                    padding: "0 4px",
                                                    position: "absolute",
                                                    top: 0,
                                                    left: "8px",
                                                    transform: "translateY(-50%)",
                                                    fontSize: "10px",
                                                    color: "#666666",
                                                  }}
                                                >
                                                  ユーザー
                                                </div>
                                                <div className="template_tips">
                                                  {
                                                    detail.permission.viewable.members.map((member) => (
                                                      <div
                                                        className="template_tip"
                                                        key={`member_${member.id}`}
                                                      >
                                                        {member.name}
                                                      </div>
                                                    ))
                                                  }
                                                </div>
                                              </div>
                                            </>
                                          )
                                        }
                                      </div>
                                    </>
                                  ) : (
                                    <></>
                                  )
                                }
                                {
                                  (detail.permission.settable.teams.length || detail.permission.settable.members.length) ? (
                                    <>
                                      <div className="template_detail">
                                        <div className="template_detail_title">
                                          プロジェクト編集権限対象
                                        </div>
                                        {
                                          !!detail.permission.settable.teams?.length && (
                                            <div
                                              style={{
                                                border: "solid 1px #CCCCCC",
                                                borderRadius: "4px",
                                                padding: "16px",
                                                position: "relative",
                                                backgroundColor: "#FFFFFF",
                                                display: "flex",
                                                flexFlow: "column",
                                                gap: "8px",
                                              }}
                                            >
                                              <div
                                                style={{
                                                  backgroundColor: "#FFFFFF",
                                                  padding: "0 4px",
                                                  position: "absolute",
                                                  top: 0,
                                                  left: "8px",
                                                  transform: "translateY(-50%)",
                                                  fontSize: "10px",
                                                  color: "#666666",
                                                }}
                                              >
                                                チーム
                                              </div>
                                              {
                                                detail.permission.settable.teams.map((team) => (
                                                  <div
                                                    style={{
                                                      border: "solid 1px #CCCCCC",
                                                    }}
                                                  >
                                                    <div
                                                      style={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        alignItems: "center",
                                                        padding: "8px",
                                                        borderBottom: "solid 1px #CCCCCC",
                                                        backgroundColor: "#EEEEEE",
                                                      }}
                                                    >
                                                      <div>
                                                        {`チーム「${team.name}」の${SCOPE_LABEL[team.scope]}${team.include_nested_teams ? " + 配下チーム" : ""}`}
                                                      </div>
                                                    </div>
                                                    <div
                                                      style={{
                                                        display: "flex",
                                                        flexFlow: "column",
                                                        gap: "12px",
                                                        padding: "12px 8px 8px",
                                                      }}
                                                    >
                                                      {
                                                        ["both", "manager"].includes(team.scope) && (
                                                          <div
                                                            style={{
                                                              border: "solid 1px #CCCCCC",
                                                              borderRadius: "4px",
                                                              padding: "8px",
                                                              position: "relative",
                                                              backgroundColor: "#FFFFFF",
                                                            }}
                                                          >
                                                            <div
                                                              style={{
                                                                backgroundColor: "#FFFFFF",
                                                                padding: "0 4px",
                                                                position: "absolute",
                                                                top: 0,
                                                                left: "8px",
                                                                transform: "translateY(-50%)",
                                                                fontSize: "10px",
                                                                color: "#666666",
                                                              }}
                                                            >
                                                              マネージャー
                                                            </div>
                                                            <div
                                                              className="template_tips"
                                                              style={{
                                                                padding: "8px",
                                                              }}
                                                            >
                                                              {
                                                                buildTargetMember(team, "manager").map((member) => (
                                                                  <div
                                                                    className="template_tip"
                                                                    key={`member_${member.id}`}
                                                                  >
                                                                    {member.name}
                                                                  </div>
                                                                ))
                                                              }
                                                            </div>
                                                          </div>
                                                        )
                                                      }
                                                      {
                                                        ["both", "member"].includes(team.scope) && !team.include_nested_teams && (
                                                          <div
                                                            style={{
                                                              border: "solid 1px #CCCCCC",
                                                              borderRadius: "4px",
                                                              padding: "8px",
                                                              position: "relative",
                                                              backgroundColor: "#FFFFFF",
                                                            }}
                                                          >
                                                            <div
                                                              style={{
                                                                backgroundColor: "#FFFFFF",
                                                                padding: "0 4px",
                                                                position: "absolute",
                                                                top: 0,
                                                                left: "8px",
                                                                transform: "translateY(-50%)",
                                                                fontSize: "10px",
                                                                color: "#666666",
                                                              }}
                                                            >
                                                              このチームのメンバー
                                                            </div>
                                                            <div
                                                              className="template_tips"
                                                              style={{
                                                                padding: "8px",
                                                              }}
                                                            >
                                                              {
                                                                buildTargetMember(team, "member").map((member) => (
                                                                  <div
                                                                    className="template_tip"
                                                                    key={`member_${member.id}`}
                                                                  >
                                                                    {member.name}
                                                                  </div>
                                                                ))
                                                              }
                                                            </div>
                                                          </div>
                                                        )
                                                      }
                                                      {
                                                        ["both", "member"].includes(team.scope) && team.include_nested_teams && (
                                                          <div
                                                            style={{
                                                              border: "solid 1px #CCCCCC",
                                                              borderRadius: "4px",
                                                              padding: "8px",
                                                              position: "relative",
                                                              backgroundColor: "#FFFFFF",
                                                            }}
                                                          >
                                                            <div
                                                              style={{
                                                                backgroundColor: "#FFFFFF",
                                                                padding: "0 4px",
                                                                position: "absolute",
                                                                top: 0,
                                                                left: "8px",
                                                                transform: "translateY(-50%)",
                                                                fontSize: "10px",
                                                                color: "#666666",
                                                              }}
                                                            >
                                                              このチームのメンバー + 配下チームに所属している全てのマネージャー・メンバー
                                                            </div>
                                                            <div
                                                              className="template_tips"
                                                              style={{
                                                                padding: "8px",
                                                              }}
                                                            >
                                                              {
                                                                Array.from(new Map([
                                                                  ...getAllMemberList(team.id || null),
                                                                  ...buildTargetMember(team, "member"),
                                                                ].map((w) => [w.id, w])).values()).sort((a, b) => a.id - b.id).map((member) => (
                                                                  <div
                                                                    className="template_tip"
                                                                    key={`member_${member.id}`}
                                                                  >
                                                                    {member.name}
                                                                  </div>
                                                                ))
                                                              }
                                                            </div>
                                                          </div>
                                                        )
                                                      }
                                                    </div>
                                                  </div>
                                                ))
                                              }
                                            </div>
                                          )
                                        }
                                        {
                                          !!detail.permission.settable.members?.length && (
                                            <>
                                              <div
                                                style={{
                                                  border: "solid 1px #CCCCCC",
                                                  borderRadius: "4px",
                                                  padding: "16px",
                                                  position: "relative",
                                                  backgroundColor: "#FFFFFF",
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    backgroundColor: "#FFFFFF",
                                                    padding: "0 4px",
                                                    position: "absolute",
                                                    top: 0,
                                                    left: "8px",
                                                    transform: "translateY(-50%)",
                                                    fontSize: "10px",
                                                    color: "#666666",
                                                  }}
                                                >
                                                  ユーザー
                                                </div>
                                                <div className="template_tips">
                                                  {
                                                    detail.permission.settable.members.map((member) => (
                                                      <div
                                                        className="template_tip"
                                                        key={`member_${member.id}`}
                                                      >
                                                        {member.name}
                                                      </div>
                                                    ))
                                                  }
                                                </div>
                                              </div>
                                            </>
                                          )
                                        }
                                      </div>
                                    </>
                                  ) : (
                                    <></>
                                  )
                                }
                                {
                                  (detail.permission.usable.teams.length || detail.permission.usable.members.length) ? (
                                    <>
                                      <div className="template_detail">
                                        <div className="template_detail_title">
                                          プロジェクト業務仕分け対象
                                        </div>
                                        {
                                          !!detail.permission.usable.teams?.length && (
                                            <div
                                              style={{
                                                border: "solid 1px #CCCCCC",
                                                borderRadius: "4px",
                                                padding: "16px",
                                                position: "relative",
                                                backgroundColor: "#FFFFFF",
                                                display: "flex",
                                                flexFlow: "column",
                                                gap: "8px",
                                              }}
                                            >
                                              <div
                                                style={{
                                                  backgroundColor: "#FFFFFF",
                                                  padding: "0 4px",
                                                  position: "absolute",
                                                  top: 0,
                                                  left: "8px",
                                                  transform: "translateY(-50%)",
                                                  fontSize: "10px",
                                                  color: "#666666",
                                                }}
                                              >
                                                チーム
                                              </div>
                                              {
                                                detail.permission.usable.teams.map((team) => (
                                                  <div
                                                    style={{
                                                      border: "solid 1px #CCCCCC",
                                                    }}
                                                  >
                                                    <div
                                                      style={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        alignItems: "center",
                                                        padding: "8px",
                                                        borderBottom: "solid 1px #CCCCCC",
                                                        backgroundColor: "#EEEEEE",
                                                      }}
                                                    >
                                                      <div>
                                                        {`チーム「${team.name}」の${SCOPE_LABEL[team.scope]}${team.include_nested_teams ? " + 配下チーム" : ""}`}
                                                      </div>
                                                    </div>
                                                    <div
                                                      style={{
                                                        display: "flex",
                                                        flexFlow: "column",
                                                        gap: "12px",
                                                        padding: "12px 8px 8px",
                                                      }}
                                                    >
                                                      {
                                                        ["both", "manager"].includes(team.scope) && (
                                                          <div
                                                            style={{
                                                              border: "solid 1px #CCCCCC",
                                                              borderRadius: "4px",
                                                              padding: "8px",
                                                              position: "relative",
                                                              backgroundColor: "#FFFFFF",
                                                            }}
                                                          >
                                                            <div
                                                              style={{
                                                                backgroundColor: "#FFFFFF",
                                                                padding: "0 4px",
                                                                position: "absolute",
                                                                top: 0,
                                                                left: "8px",
                                                                transform: "translateY(-50%)",
                                                                fontSize: "10px",
                                                                color: "#666666",
                                                              }}
                                                            >
                                                              マネージャー
                                                            </div>
                                                            <div
                                                              className="template_tips"
                                                              style={{
                                                                padding: "8px",
                                                              }}
                                                            >
                                                              {
                                                                buildTargetMember(team, "manager").map((member) => (
                                                                  <div
                                                                    className="template_tip"
                                                                    key={`member_${member.id}`}
                                                                  >
                                                                    {member.name}
                                                                  </div>
                                                                ))
                                                              }
                                                            </div>
                                                          </div>
                                                        )
                                                      }
                                                      {
                                                        ["both", "member"].includes(team.scope) && !team.include_nested_teams && (
                                                          <div
                                                            style={{
                                                              border: "solid 1px #CCCCCC",
                                                              borderRadius: "4px",
                                                              padding: "8px",
                                                              position: "relative",
                                                              backgroundColor: "#FFFFFF",
                                                            }}
                                                          >
                                                            <div
                                                              style={{
                                                                backgroundColor: "#FFFFFF",
                                                                padding: "0 4px",
                                                                position: "absolute",
                                                                top: 0,
                                                                left: "8px",
                                                                transform: "translateY(-50%)",
                                                                fontSize: "10px",
                                                                color: "#666666",
                                                              }}
                                                            >
                                                              このチームのメンバー
                                                            </div>
                                                            <div
                                                              className="template_tips"
                                                              style={{
                                                                padding: "8px",
                                                              }}
                                                            >
                                                              {
                                                                buildTargetMember(team, "member").map((member) => (
                                                                  <div
                                                                    className="template_tip"
                                                                    key={`member_${member.id}`}
                                                                  >
                                                                    {member.name}
                                                                  </div>
                                                                ))
                                                              }
                                                            </div>
                                                          </div>
                                                        )
                                                      }
                                                      {
                                                        ["both", "member"].includes(team.scope) && team.include_nested_teams && (
                                                          <div
                                                            style={{
                                                              border: "solid 1px #CCCCCC",
                                                              borderRadius: "4px",
                                                              padding: "8px",
                                                              position: "relative",
                                                              backgroundColor: "#FFFFFF",
                                                            }}
                                                          >
                                                            <div
                                                              style={{
                                                                backgroundColor: "#FFFFFF",
                                                                padding: "0 4px",
                                                                position: "absolute",
                                                                top: 0,
                                                                left: "8px",
                                                                transform: "translateY(-50%)",
                                                                fontSize: "10px",
                                                                color: "#666666",
                                                              }}
                                                            >
                                                              このチームのメンバー + 配下チームに所属している全てのマネージャー・メンバー
                                                            </div>
                                                            <div
                                                              className="template_tips"
                                                              style={{
                                                                padding: "8px",
                                                              }}
                                                            >
                                                              {
                                                                Array.from(new Map([
                                                                  ...getAllMemberList(team.id || null),
                                                                  ...buildTargetMember(team, "member"),
                                                                ].map((w) => [w.id, w])).values()).sort((a, b) => a.id - b.id).map((member) => (
                                                                  <div
                                                                    className="template_tip"
                                                                    key={`member_${member.id}`}
                                                                  >
                                                                    {member.name}
                                                                  </div>
                                                                ))
                                                              }
                                                            </div>
                                                          </div>
                                                        )
                                                      }
                                                    </div>
                                                  </div>
                                                ))
                                              }
                                            </div>
                                          )
                                        }
                                        {
                                          !!detail.permission.usable.members?.length && (
                                            <>
                                              <div
                                                style={{
                                                  border: "solid 1px #CCCCCC",
                                                  borderRadius: "4px",
                                                  padding: "16px",
                                                  position: "relative",
                                                  backgroundColor: "#FFFFFF",
                                                }}
                                              >
                                                <div
                                                  style={{
                                                    backgroundColor: "#FFFFFF",
                                                    padding: "0 4px",
                                                    position: "absolute",
                                                    top: 0,
                                                    left: "8px",
                                                    transform: "translateY(-50%)",
                                                    fontSize: "10px",
                                                    color: "#666666",
                                                  }}
                                                >
                                                  ユーザー
                                                </div>
                                                <div className="template_tips">
                                                  {
                                                    detail.permission.usable.members.map((member) => (
                                                      <div
                                                        className="template_tip"
                                                        key={`member_${member.id}`}
                                                      >
                                                        {member.name}
                                                      </div>
                                                    ))
                                                  }
                                                </div>
                                              </div>
                                            </>
                                          )
                                        }
                                      </div>
                                    </>
                                  ) : (
                                    <></>
                                  )
                                }
                                {
                                  detail.labels.length ? (
                                    <>
                                      <div className="template_detail">
                                        <div className="template_detail_title">
                                          業務：
                                        </div>
                                        <div className="template_tips">
                                          {
                                            detail.labels.map((label) => (
                                              <div
                                                key={`${detail.name}_${label.name}`}
                                                className="template_tip"
                                              >
                                                {label.name}
                                              </div>
                                            ))
                                          }
                                        </div>
                                      </div>
                                    </>
                                  ) : (
                                    <></>
                                  )
                                }
                                <div
                                  className="template_button_area"
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "right",
                                    gap: "8px",
                                  }}
                                >
                                  <button
                                    type="button"
                                    onClick={() => deleteTemplate(detail?.id || 0, `ユーザーテンプレート:${detail.name}`)}
                                    style={{
                                      backgroundColor: "#C61616",
                                      border: "1px solid #C61616",
                                    }}
                                  >
                                    削除する
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => clickSelect(detail)}
                                  >
                                    選択する
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => clickEdit(detail?.id || 0)}
                                    style={{
                                      backgroundColor: "#4472ba",
                                      border: "1px solid #4472ba",
                                    }}
                                  >
                                    編集する
                                  </button>
                                </div>
                              </div>
                            ))
                          }
                          {
                            !!selectList.length && (
                              <div className="button_area">
                                <button
                                  type="button"
                                  className="button"
                                  onClick={createTemplate}
                                >
                                  <FontAwesomeIcon icon={['fas', 'plus']} />
                                  &nbsp;
                                  テンプレートを新規作成する
                                </button>
                              </div>
                            )
                          }
                        </>
                        {
                          !selectList.length && step === "custom" ? (
                            <>
                              <div className="no_data_area">
                                <div className="no_data">
                                  テンプレートはありません
                                </div>
                                <div className="button_area">
                                  <button
                                    type="button"
                                    className="button"
                                    onClick={createTemplate}
                                  >
                                    <FontAwesomeIcon icon={['fas', 'plus']} />
                                    &nbsp;
                                    テンプレートを新規作成する
                                  </button>
                                </div>
                              </div>
                            </>
                          ) : (
                            <></>
                          )
                        }
                      </div>
                    </>
                  )
                }
              </>
            )
          }
        </>
      </Modal>
      <ApiRequestModal
        display={isOpenApiRequestModal}
        closeCallback={() => setIsOpenApiRequestModal(false)}
      />
    </>
  )
}

CreateProjectModal.defaultProps = {
  deleteCallback: undefined,
  startCustom: false,
}

export default CreateProjectModal
